import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BoardApi } from '../../shared/api/BoardApi';
import { RootState } from '../store';

interface Task {
    id: number;
    title?: string;
    description: string;
    key: any;
    sequence: number;
    taskStatus: any;
    taskIssueType: any;
    dueDate?: any;
    assignee?: any;
}

interface ProjectMilestones {
    name: string;
    milestoneId: number;
}

interface Projectuser {
    userId: number;
    userName: string;
}

interface ProjectTaskdetails {
    id: number;
    summary: string;
    description: string;
    key: string;
    sequence: number;
    taskStatus: string;
    taskIssueType: string;
    taskPriority: string;
    originalEstimateTime: string;
    projectMilestoneId: number;
    startDate: string;
    onHoldComment: string;
    onHoldDate: string;
    sentToApprovalDate: string;
    declineComment: string;
    completedDate: string;
    approvalDate: string;
    approvalComment: string;
    timeLogsForTask: {
        currenetPage: number;
        hasNext: boolean;
        hasPrevious: boolean;
        number: number;
        numberOfElement: number;
        totalTime: string;
        timeLogs: timeLog[];
    }
    userId: number;
}

interface timeLog {
    id: number;
    userId: number;
    userName: string;
    taskId: number;
    logDate: string;
    timeSpent: string;
    description: string;
}

interface TimeLogsResponse {
    number: number;
    numberOfElements: number;
    timeLogs: timeLog[];
    totalTime: string;
    hasPrevious: boolean;
    hasNext: boolean;
    currentPage: number;
} 

interface BoardState {
    showNewTaskInput: boolean;
    toggleNewTaskInput: boolean;
    hideNewTaskInput: boolean;
    tasks: Task[];
    updateTasks: updateTasks[];
    selectedTask: {
        taskId: number;
        taskName: string | null;
        taskStatus: string | null;
        isShow: boolean;
    }
    projectMilestones: ProjectMilestones[];
    projectUser: Projectuser[];
    projectTaskdetails: ProjectTaskdetails | null;
    timeLogsResponse: TimeLogsResponse | null;
}

const initialState: BoardState = {
    showNewTaskInput: false,
    toggleNewTaskInput: false,
    hideNewTaskInput: false,
    tasks: [],
    updateTasks: [],
    selectedTask: {
        taskId: 0,
        taskName: null,
        taskStatus: null,
        isShow: false,
    },
    projectMilestones: [],
    projectUser: [],
    projectTaskdetails: null,
    timeLogsResponse: null,
};

interface updateTasks {
    id: number;
    title?: string;
    description: string;
    key: any;
    sequence: number;
    taskStatus: any;
    taskIssueType: any;
    dueDate?: any;
    assignee?: any;
}

const boardSlice = createSlice({
    name: 'board',
    initialState,
    reducers: {
        toggleNewTaskInput: (state) => {
            state.showNewTaskInput = !state.showNewTaskInput;
        },
        showNewTaskInput: (state) => {
            state.showNewTaskInput = true;
        },
        hideNewTaskInput: (state) => {
            state.showNewTaskInput = false;
        },
        updateTasks: (state, action) => {
            state.tasks = action.payload;
        },
        setSelectedTask: (state, action: PayloadAction<{ taskId: number; taskName: string; taskStatus: string; isShow: boolean}>) => {
            state.selectedTask = {
                taskId: action.payload.taskId,
                taskName: action.payload.taskName,
                taskStatus: action.payload.taskStatus,
                isShow: action.payload.isShow,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                BoardApi.endpoints.searchTask.matchFulfilled,
                (state, action) => {
                    if (action.payload && action.payload.projectTasks) {
                        state.tasks = action.payload.projectTasks;
                        state.projectMilestones = action.payload.projectMilestones;
                        state.projectUser = action.payload.projectUser;
                    }
                }
            )

            .addMatcher(
                BoardApi.endpoints.reorderTasks.matchFulfilled,
                (state, action) => {
                    if (action.payload && action.payload.projectTasks) {
                        state.updateTasks = action.payload.projectTasks;
                    }
                }
            )

            .addMatcher(
                BoardApi.endpoints.getProjectTask.matchFulfilled,
                (state, action) => {
                    if (action.payload) {
                        state.projectTaskdetails = action.payload;
                    }
                }
            )

            .addMatcher(
                BoardApi.endpoints.getTimeLogs.matchFulfilled,
                (state, action) => {
                    if (action.payload) {
                        // Correctly assign timeLogsResponse ensuring it matches the structure
                        state.timeLogsResponse = {
                            ...action.payload,
                            timeLogs: action.payload.timeLogs.map(log => ({
                                ...log // Spread to ensure it is a new object
                            })),
                        };
                    }
                }
            )

            .addMatcher(
                BoardApi.endpoints.searchTask.matchFulfilled,
                (state, action) => {
                    if (action.payload && action.payload.projectTasks) {
                        state.tasks = action.payload.projectTasks;
                    }
                }
            )
    }
});

export const { toggleNewTaskInput, showNewTaskInput, hideNewTaskInput, updateTasks, setSelectedTask } = boardSlice.actions;
export const selectSelectedTask = (state: RootState) => state.board.selectedTask;
export const boardReducer = boardSlice.reducer;
export const projectMilestones = (state: RootState) => state.board.projectMilestones;
export const projectUser = (state: RootState) => state.board.projectUser;
export const selectProjectTaskDetails = (state: RootState) => state.board.projectTaskdetails;
export const timeLogs = (state: RootState) => state.board.timeLogsResponse;
export const searchingTask = (state: RootState) => state.board.tasks;
